import { useState, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  sendMillingEmail,
  resetMillingEmailState,
} from "../../store/admin/millingSlice";
import { useParams } from "react-router-dom";
import { useToast } from "../utilities/Toast";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";

const MillingEmail = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { caseFiles, fetching } = useAppSelector((state) => state.fileUpload);
  const { loading, success } = useAppSelector((state) => state.millingemails);
  const { case_id } = useParams<{ case_id: string }>();

  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Fetch case files and passkeys safely
  useEffect(() => {
    if (case_id) {
      dispatch(fetchCaseFilesByCaseId({ case_id }));
    }
  }, [case_id, dispatch]);

  // Filter "downloads" type files
  const downloadFiles = useMemo(
    () => caseFiles.filter((f) => f.file_type?.toLowerCase() === "ts_download"),
    [caseFiles]
  );

  // Group by folder name
  const filesByFolder = useMemo(() => {
    return downloadFiles.reduce<Record<string, typeof downloadFiles>>(
      (acc, f) => {
        const folder = f.folder_name || "Unknown";
        if (!acc[folder]) acc[folder] = [];
        acc[folder].push(f);
        return acc;
      },
      {}
    );
  }, [downloadFiles]);

  const isReadOnly = ["Admin Read", "Production", "Side Admin"].includes(
    user?.role || ""
  );

  const toggleSelect = (fileId: string) => {
    if (isReadOnly) return;
    setSelectedFileIds((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleConfirm = () => {
    dispatch(
      sendMillingEmail({
        file_ids: selectedFileIds,
        sendBy:
          user?.role === "Super Admin"
            ? user?.user_name
            : employee?.emp_name || "",
      })
    );
    setShowModal(false);
    setSelectedFileIds([]);
  };

  // Handle success toast
  useEffect(() => {
    if (success) {
      showToast("Milling email generated successfully!", "success");
      const timeout = setTimeout(() => {
        dispatch(resetMillingEmailState());
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [success, dispatch, showToast]);

  return (
    <div className="w-full px-6 py-4 space-y-6">
      <h2 className="text-lg font-semibold text-black">
        Select Files for Milling Email
      </h2>

      {fetching && <p className="text-gray-500 italic">Loading files...</p>}
      {!fetching && downloadFiles.length === 0 && (
        <p className="text-gray-500">No files available</p>
      )}

      {/* Grouped rendering by folder */}
      {Object.entries(filesByFolder).map(([folder, files]) => (
        <div key={folder} className="mb-4">
          <h3 className="font-medium text-black mb-2">{folder}</h3>
          <ul className="space-y-2">
            {files.map((file, idx) => (
              <li key={file.fileId || idx} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  disabled={isReadOnly}
                  checked={selectedFileIds.includes(String(file.fileId))}
                  onChange={() => toggleSelect(String(file.fileId))}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-gray-700 text-sm">{file.file_name}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {!isReadOnly && selectedFileIds.length > 0 && (
        <button
          onClick={() => setShowModal(true)}
          className="px-5 py-2 bg-[#2fa9bf] text-white text-sm rounded hover:bg-[#1c98ad]"
        >
          {loading ? "Sending..." : "Generate Milling Email"}
        </button>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded w-[90%] max-w-md space-y-4">
            <h3 className="text-md font-semibold">Confirm Email Generation</h3>

            <ul className="list-disc ml-5 text-sm text-gray-700">
              {downloadFiles
                .filter((f) => selectedFileIds.includes(String(f.fileId)))
                .map((f) => (
                  <li key={f.fileId}>{f.file_name}</li>
                ))}
            </ul>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowModal(false);
                }}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 text-sm bg-[#2fa9bf] text-white rounded hover:bg-[#1c98ad]"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MillingEmail;
